import type { DropDownNoSelection, DropDownResult, TypeOrArray } from '@myosh/odin-components';
import { DropDownMultiSelect, isDropDownNoSelection } from '@myosh/odin-components';
import { useMemo } from 'react';
import type { HierarchyType } from '../../../api/@types/hierarchy.types';
import { useHierarchyValuesQuery } from '../../../api/enhanced/enhanced-v4-api';

interface HierarchyTypeMultiSelectProps {
  type: HierarchyType;
  selectedIds: number[];
  onChange: (typeId: number, ids: number[]) => void;
}

const toSelectedIds = (value?: TypeOrArray<DropDownResult> | DropDownNoSelection): number[] => {
  if (!value || isDropDownNoSelection(value)) {
    return [];
  }
  const values = Array.isArray(value) ? value : [value];
  return values.map((result) => Number(result.value));
};

/**
 * Renders a multi-select of the values for a single hierarchy type. Fetches the type's values from
 * the hierarchy-values endpoint (one request per rendered type) to populate the options and resolve
 * the current selection's captions.
 */
function HierarchyTypeMultiSelect({ type, selectedIds, onChange }: HierarchyTypeMultiSelectProps) {
  // POC limitation: values are loaded as a single page (first 200, see hierarchyValues query). Editing
  // a type with more than 200 values could drop selections beyond the first page. Untouched types keep
  // their full saved selection (the working copy is hydrated from the saved scope, not from this page).
  const { data: values = [] } = useHierarchyValuesQuery({ typeId: type.id });

  const value = useMemo<DropDownResult[]>(
    () =>
      values
        .filter((hierarchyValue) => selectedIds.includes(hierarchyValue.id))
        .map((hierarchyValue) => ({ value: hierarchyValue.id, text: hierarchyValue.caption })),
    [values, selectedIds]
  );

  return (
    <DropDownMultiSelect
      name={String(type.id)}
      label={type.caption}
      data={values}
      value={value}
      allowClear
      placeholder={`Select ${type.caption}`}
      textField="caption"
      valueField="id"
      onChange={(changed) => onChange(type.id, toSelectedIds(changed))}
      labelStyles="font-bold text-xs text-gray-1"
      className="py-0.5 pl-1.5 text-gray-2"
    />
  );
}

export default HierarchyTypeMultiSelect;
